import React, { PureComponent, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { ReferencedProductType } from '~/Api/SiPriceReferencing/MaxPrice';
import { formatNumericHumanDate, parseNumericHumanDate } from '~/DateTime';
import { DebounceInput } from '~/Debounce';
import DatePickerInput from '~/UI/DatePickerInput/DatePickerInput';
import Icon from '~/UI/Icon/Icon';
import defaultInputStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';
import Tooltip from '~/UI/Tooltip/Tooltip';

import styles from './styles.less';

export interface Value {
	value: string;
	isValid: boolean | null;
}

export interface Values {
	packageSize: Value;
	basketVersion: string;
	dosageStrength: Value;
	productType: string;
	historicProductDataViewDate: {
		value: string;
		dateValue: Date | null;
		isValid: boolean | null;
	};
}

interface Props {
	values: Values;
	versions: ReferenceBasketVersion[] | null;
	productTypes: ReferencedProductType[] | null;
	updateStatus: Status | null;
	onChange(values: Values): void;
	onSave(): void;
}

export default class ParametersPanel extends PureComponent<Props> {
	renderVersionSelect() {
		const { versions, values, onChange } = this.props;
		const currentVersion = versions?.find((version) => version.id === values.basketVersion);

		const descriptionIcon = (
			<span className={styles.parameterIcon}>
				<Icon name='violet-question-mark' />
			</span>
		);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={versions === null}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={values.basketVersion}
						onChange={(event) => {
							onChange({ ...values, basketVersion: event.target.value });
						}}
					>
						{versions?.map((version) => (
							<option key={version.id} value={version.id}>
								Since {formatNumericHumanDate(version.sinceDate)}
							</option>
						))}
					</Select>

					{currentVersion !== undefined ? (
						<Tooltip reference={descriptionIcon}>{currentVersion.description}</Tooltip>
					) : null}
				</div>
			</label>
		);
	}

	renderProductTypeSelect() {
		const { productTypes, values, onChange } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Method:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={!productTypes}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={values.productType}
						onChange={(event) => {
							onChange({ ...values, productType: event.target.value });
						}}
					>
						{productTypes
							? productTypes.map((productType) => (
									<option key={productType.id} value={productType.id}>
										{productType.title}
									</option>
							  ))
							: null}
					</Select>
				</div>
			</label>
		);
	}

	renderReferencePackageSize() {
		const { values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-package-size'>
				<span className={styles.parameterLabelText}>Package size:</span>
				<div className={styles.parameterInput}>
					<DebounceInput
						value={values.packageSize.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, packageSize: { value, isValid: null } });
						}}
					>
						<Input
							id='reference-package-size'
							isInvalid={values.packageSize.isValid === false}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	renderDosageStrength() {
		const { values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-dosage-strength'>
				<span className={styles.parameterLabelText}>Dosage strength:</span>
				<div className={styles.parameterInput}>
					<DebounceInput
						value={values.dosageStrength.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, dosageStrength: { value, isValid: null } });
						}}
					>
						<Input
							id='reference-dosage-strength'
							isInvalid={values.dosageStrength.isValid === false}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderHistoricViewDateInput(): ReactNode {
		const { values, onChange } = this.props;
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-historic-date'>
				<span className={styles.parameterLabelText}>Reference date:</span>
				<div className={styles.dateParameterInput}>
					<DatePickerInput
						clearable
						dateValue={values.historicProductDataViewDate.dateValue}
						disabledDays={{ after: new Date(), before: yearAgo }}
						isInvalid={values.historicProductDataViewDate.isValid === false}
						placeholder='Today'
						styles={defaultInputStyles}
						value={values.historicProductDataViewDate.value}
						onChange={(value) => {
							onChange({
								...values,
								historicProductDataViewDate: {
									value,
									dateValue: parseNumericHumanDate(value),
									isValid: null,
								},
							});
						}}
					/>
				</div>
			</label>
		);
	}

	render() {
		const { updateStatus } = this.props;

		return (
			<div className={styles.container} data-testid='parameters-panel'>
				<div className={styles.content}>
					<div className={styles.parameters}>
						<p className={styles.parametersDescription}>Maximum price calculation parameters:</p>

						{this.renderHistoricViewDateInput()}
						{this.renderVersionSelect()}
						{this.renderProductTypeSelect()}
						{this.renderReferencePackageSize()}
						{this.renderDosageStrength()}

						<SaveActivityIndicator
							hideAfterMilliseconds={3000}
							status={updateStatus}
							testId='parameters-panel-save-indicator'
						/>
					</div>
				</div>
			</div>
		);
	}
}
